import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { CreditCard, DollarSign, Smartphone, ArrowRight } from "lucide-react";

const METHODS = [
  { key: "stripe", label: "Pay by Card", desc: "Secure Stripe checkout. Visa, Mastercard, Amex.", icon: <CreditCard size={18}/> },
  { key: "cashapp", label: "Cash App", desc: "Send to $MickeyTrivett. Manual confirmation.", icon: <DollarSign size={18}/> },
  { key: "venmo", label: "Venmo", desc: "Send to @MickeyTrivett. Manual confirmation.", icon: <Smartphone size={18}/> },
];

export default function Checkout() {
  const { items, clear } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [addr, setAddr] = useState({ line1: "", line2: "", city: "", state: "", zip: "", country: "US" });
  const [method, setMethod] = useState("stripe");
  const [quote, setQuote] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [isGift, setIsGift] = useState(false);
  const [giftRecipient, setGiftRecipient] = useState("");
  const [giftMessage, setGiftMessage] = useState("");

  useEffect(() => {
    if (user) { setEmail(user.email); setName(user.name || ""); }
  }, [user]);

  useEffect(() => {
    if (!items.length) return;
    const payload = items.map(it => ({
      artwork_id: it.artwork_id, variant_key: it.variant_key,
      quantity: it.quantity, personal_text: it.personal_text || null,
    }));
    http.post("/cart/quote", payload).then(({ data }) => setQuote(data));
  }, [items]);

  if (!items.length) {
    return (
      <Layout>
        <div className="max-w-[800px] mx-auto px-6 py-32 text-center">
          <h2 className="text-grunge text-3xl text-[#F5F2EB] mb-4">Your cart is empty.</h2>
          <Link to="/shop" className="btn-copper">Shop the Collection</Link>
        </div>
      </Layout>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    const payloadItems = items.map(it => ({
      artwork_id: it.artwork_id, variant_key: it.variant_key,
      quantity: it.quantity, personal_text: it.personal_text || null,
    }));
    const qrCode = items.find(it => it.qr_code)?.qr_code || null;
    const giftCardCode = localStorage.getItem("stc_gift_code") || null;
    setSubmitting(true);
    try {
      if (method === "stripe") {
        const { data } = await http.post("/checkout/stripe", {
          items: payloadItems,
          email, shipping_address: addr,
          origin_url: window.location.origin,
          qr_code: qrCode,
          gift_card_code: giftCardCode,
          is_gift: isGift,
          gift_recipient_name: isGift ? giftRecipient || null : null,
          gift_message: isGift ? giftMessage || null : null,
        });
        // Stash order ID so success page can show fallback details
        sessionStorage.setItem("stc_last_session", data.session_id);
        sessionStorage.setItem("stc_last_order", data.order_number);
        clear();
        localStorage.removeItem("stc_gift_code");
        window.location.href = data.url;
      } else {
        const { data } = await http.post("/checkout/manual", {
          items: payloadItems,
          email, name,
          shipping_address: addr,
          payment_method: method,
          qr_code: qrCode,
          gift_card_code: giftCardCode,
          is_gift: isGift,
          gift_recipient_name: isGift ? giftRecipient || null : null,
          gift_message: isGift ? giftMessage || null : null,
        });
        clear();
        localStorage.removeItem("stc_gift_code");
        nav(`/proof/${data.order_number}?manual=1`);
        toast.success(`Order ${data.order_number} created. Follow instructions to send payment.`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <section className="bg-black pt-24 pb-24">
        <div className="max-w-[1280px] mx-auto px-6 md:px-12">
          <h1 className="text-grunge text-5xl text-[#F5F2EB] mb-12">FINISH THE STORY.</h1>

          <form onSubmit={submit} className="grid lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2 space-y-8">
              <div>
                <div className="font-ui text-[10px] tracking-[0.32em] text-[#B8915A] uppercase mb-4">Contact</div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <input data-testid="co-email" required type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email" className="stc-input"/>
                  <input data-testid="co-name" value={name} onChange={e => setName(e.target.value)} placeholder="Full Name" className="stc-input"/>
                </div>
              </div>

              <div>
                <div className="font-ui text-[10px] tracking-[0.32em] text-[#B8915A] uppercase mb-4">Shipping Address</div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <input data-testid="co-line1" value={addr.line1} onChange={e => setAddr({...addr, line1: e.target.value})} placeholder="Street Address" className="stc-input sm:col-span-2"/>
                  <input value={addr.line2} onChange={e => setAddr({...addr, line2: e.target.value})} placeholder="Apt / Suite (optional)" className="stc-input sm:col-span-2"/>
                  <input data-testid="co-city" value={addr.city} onChange={e => setAddr({...addr, city: e.target.value})} placeholder="City" className="stc-input"/>
                  <input data-testid="co-state" value={addr.state} onChange={e => setAddr({...addr, state: e.target.value})} placeholder="State" className="stc-input"/>
                  <input data-testid="co-zip" value={addr.zip} onChange={e => setAddr({...addr, zip: e.target.value})} placeholder="ZIP" className="stc-input"/>
                  <input value={addr.country} onChange={e => setAddr({...addr, country: e.target.value})} placeholder="Country" className="stc-input"/>
                </div>
              </div>

              <div>
                <div className="font-ui text-[10px] tracking-[0.32em] text-[#B8915A] uppercase mb-4">Gift Mode</div>
                <label className={`flex gap-4 items-start p-4 border cursor-pointer ${isGift ? "border-[#B8915A] bg-[#B8915A]/5" : "border-white/10 hover:border-white/30"}`}>
                  <input type="checkbox" data-testid="co-gift-toggle"
                    checked={isGift} onChange={(e)=>setIsGift(e.target.checked)}
                    className="mt-1 accent-[#B8915A]"/>
                  <div className="flex-1">
                    <div className="font-ui text-sm tracking-[0.16em] uppercase text-[#F5F2EB]">This is a gift</div>
                    <div className="font-body text-sm text-[#A3A3A3] mt-1">
                      The recipient will receive a unique QR claim link with their certificate
                      so they can register the Truth in their own name.
                    </div>
                  </div>
                </label>
                {isGift && (
                  <div className="mt-3 grid sm:grid-cols-2 gap-3" data-testid="co-gift-fields">
                    <input value={giftRecipient} onChange={(e)=>setGiftRecipient(e.target.value)}
                      data-testid="co-gift-recipient" placeholder="Recipient's name (optional)"
                      className="stc-input"/>
                    <input value={giftMessage} onChange={(e)=>setGiftMessage(e.target.value)}
                      data-testid="co-gift-message" placeholder="Short note (optional)"
                      className="stc-input"/>
                  </div>
                )}
              </div>

              <div>
                <div className="font-ui text-[10px] tracking-[0.32em] text-[#B8915A] uppercase mb-4">Payment</div>
                <div className="space-y-3" data-testid="payment-methods">
                  {METHODS.map(m => (
                    <label key={m.key} className={`flex gap-4 items-start p-4 border cursor-pointer ${method === m.key ? "border-[#B8915A] bg-[#B8915A]/5" : "border-white/10 hover:border-white/30"}`}>
                      <input type="radio" name="m" data-testid={`pay-${m.key}`} value={m.key} checked={method === m.key} onChange={() => setMethod(m.key)} className="mt-1 accent-[#B8915A]"/>
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <span className="text-[#B8915A]">{m.icon}</span>
                          <span className="font-ui text-sm tracking-[0.16em] uppercase text-[#F5F2EB]">{m.label}</span>
                        </div>
                        <div className="font-body text-sm text-[#A3A3A3] mt-1">{m.desc}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <aside className="border border-white/10 p-6 bg-[#0a0a0a] h-fit sticky top-28">
              <div className="font-ui text-[10px] tracking-[0.32em] text-[#B8915A] uppercase mb-4">Summary</div>
              <div className="space-y-2 font-body text-[#F5F2EB]">
                <div className="flex justify-between text-sm"><span>Subtotal</span><span>${(quote?.subtotal ?? 0).toFixed(2)}</span></div>
                <div className="flex justify-between text-sm"><span>Shipping</span><span>${(quote?.shipping ?? 0).toFixed(2)}</span></div>
              </div>
              <div className="border-t border-white/10 pt-4 mt-4 flex justify-between items-baseline">
                <span className="font-ui text-[11px] tracking-[0.22em] uppercase text-[#737373]">Total</span>
                <span data-testid="co-total" className="text-grunge text-3xl text-[#F5F2EB]">${(quote?.total ?? 0).toFixed(2)}</span>
              </div>
              <button data-testid="co-submit" type="submit" disabled={submitting} className="btn-copper w-full mt-6 justify-center disabled:opacity-60">
                {submitting ? "Processing…" : method === "stripe" ? "Pay Securely" : `Place Order via ${method === "cashapp" ? "Cash App" : "Venmo"}`}
                <ArrowRight size={16}/>
              </button>
              <p className="font-body text-xs text-[#737373] mt-3 text-center">By placing this order you agree to the Simple Truths terms.</p>
            </aside>
          </form>
        </div>
      </section>
    </Layout>
  );
}
